import companyIcon from 'assets/icons/building.svg';
import chevronUpIcon from 'assets/icons/chevron-up.svg';
import externalLinkIcon from 'assets/icons/external-link-alt.svg';
import apiIcon from 'assets/icons/psd-2.svg';
import { AccountsContext } from 'context/accounts/AccountsContext';
import { RolesContext } from 'context/roles/RolesContext';
import React, { Fragment, useContext } from 'react';
import { Collapse, Link, LocaleContext } from 'shared';
import { Account, OWNER_ROLE } from 'store/api/types';
import { getEnvironmentVar } from 'utils';

import { AccountsDropdown } from './accountsDropdown/AccountsDropdown';
import { SidebarMenuItem, SidebarSubmenu, SidebarSubmenuItem, SidebarWrapper } from './Sidebar.styles';
import { SidebarProps } from './Sidebar.types';

export const Sidebar = ({ location: { pathname }, history, onAddAccount }: SidebarProps) => {
  const accountsContext = useContext(AccountsContext);
  const localeContext = useContext(LocaleContext);
  const rolesContext = useContext(RolesContext);
  const handleSetActiveAccount = (uuid: string) => accountsContext.setActiveAccount(uuid);
  const activeAccount = accountsContext.activeAccount as Account;
  const hasOwnership = activeAccount && rolesContext.getRoleInAccount(activeAccount.uuid) === OWNER_ROLE;

  const apiMenuItemRenderer = (collapsed: boolean) => (
    <SidebarMenuItem className="sidebar__menu-item" active={pathname.includes(`/${activeAccount.uuid}/dashboard`)}>
      <img src={apiIcon} alt={localeContext.formatMessage({ id: 'SIDEBAR.PSD2' })} />
      {localeContext.formatMessage({ id: 'SIDEBAR.PSD2' })}
      <img
        src={chevronUpIcon}
        className={`collapse-icon ${collapsed ? 'collapse-icon--collapsed' : 'collapse-icon--not-collapsed'}`}
        alt={localeContext.formatMessage({ id: 'SIDEBAR.PSD2' })}
      />
    </SidebarMenuItem>
  );

  if (!activeAccount) {
    return null;
  }

  return (
    <SidebarWrapper className="sidebar">
      <AccountsDropdown
        activeItem={{
          key: activeAccount.uuid,
          label: activeAccount.name,
        }}
        items={accountsContext.accounts
          .filter(a => a.uuid !== activeAccount.uuid)
          .map(a => ({ key: a.uuid, label: a.name }))}
        onSelect={handleSetActiveAccount}
      />

      <Collapse collapsed={false} title={apiMenuItemRenderer}>
        <SidebarSubmenu className="sidebar__sub-menu">
          <SidebarSubmenuItem
            className="sub-menu__item"
            active={pathname.includes(`/${activeAccount.uuid}/dashboard`)}
            as={Link}
            {...{ url: `/${activeAccount.uuid}/dashboard` }}
          >
            {localeContext.formatMessage({ id: 'DASHBOARD.APPLICATIONS' })}
          </SidebarSubmenuItem>

          <SidebarSubmenuItem
            className="sub-menu__item"
            as={Link}
            {...{
              external: true,
              target: '_blank',
              url: `${getEnvironmentVar('REACT_APP_SPA_URL')}documentation/sandbox`,
            }}
          >
            {localeContext.formatMessage({ id: 'SIDEBAR.DOCUMENTATION' })}
            <img src={externalLinkIcon} alt={localeContext.formatMessage({ id: 'SIDEBAR.DOCUMENTATION' })} />
          </SidebarSubmenuItem>
        </SidebarSubmenu>
      </Collapse>

      {hasOwnership && (
        <Fragment>
          <SidebarMenuItem
            as={Link}
            {...{ url: `/${activeAccount.uuid}/account` }}
            active={pathname === `/${activeAccount.uuid}/account`}
            className="sidebar__menu-item"
          >
            <img src={companyIcon} alt={localeContext.formatMessage({ id: 'SIDEBAR.ACCOUNT-DETAILS' })} />
            {localeContext.formatMessage({ id: 'SIDEBAR.ACCOUNT-DETAILS' })}
          </SidebarMenuItem>
        </Fragment>
      )}
    </SidebarWrapper>
  );
};
